import * as React from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import * as moment from 'moment';
import { Avatar, DateTime, Icon, Tooltip, Button } from '../';
import MiniUserList from '../MiniUserList/MiniUserList';
import NoteForm from './NoteForm';
import { updateNote, removeNote } from '../../redux/actions/notes';

interface Props {
  note: any;
  showParentInfo: boolean;
  auth: any;
  dispatch: Function;
}

interface State {
  note: any;
  parentPath: any;
  isEditing: boolean;
  isDeleted: boolean;
}

class NoteItem extends React.Component<Props, State> {
  constructor(props) {
    super(props);
    this.state = {
      note: this.props.note,
      parentPath: '',
      isEditing: false,
      isDeleted: false,
    };
    this.handleEdit = this.handleEdit.bind(this);
    this.handleUnedit = this.handleUnedit.bind(this);
    this.handleUpdate = this.handleUpdate.bind(this);
    this.receiveUpdate = this.receiveUpdate.bind(this);
    this.handleDelete = this.handleDelete.bind(this);
    this.renderBody = this.renderBody.bind(this);
    this.renderRight = this.renderRight.bind(this);
  }

  /**
   * Determine if the current user has permission to edit/delete note
   */
  get hasPermission() {
    return (
      this.props.auth.user.id === this.state.note.user_id &&
      !this.props.showParentInfo
    );
  }

  componentDidMount() {
    if (this.props.showParentInfo) {
      switch (this.state.note.resource_type) {
        case 'vehicle_types':
          this.setState({ parentPath: '/settings/vehicles/types' });
          break;
        case 'vehicles':
          this.setState({ parentPath: '/settings/vehicles' });
          break;
        case 'holidays':
          this.setState({ parentPath: '/settings/holidays' });
          break;
        case 'accessories':
          this.setState({ parentPath: '/settings/accessories' });
          break;
        default:
          this.setState({ parentPath: `/${this.state.note.resource_type}` });
      }
    }
  }

  /**
   * Start editing a note
   */
  handleEdit() {
    this.setState({ isEditing: true });
  }

  /**
   * Cancel editing a note
   */
  handleUnedit() {
    this.setState({ isEditing: false });
  }

  /**
   * Update a note
   * @param {Object} fields
   */
  async handleUpdate(fields) {
    const response = await this.props.dispatch(
      updateNote(this.state.note.id, {
        ...fields,
        resolveable: fields.resolveable ? 1 : 0,
      }),
    );
    if (!response.error) {
      this.receiveUpdate(response.data);
    }
  }

  /**
   * Receive an update to a note
   * @param {Object} response
   */
  receiveUpdate(response) {
    this.setState({ note: response, isEditing: false });
  }

  /**
   * Delete a note
   */
  async handleDelete() {
    if (
      window.confirm(`Really delete this note by ${this.state.note.user_name}?\n
      This action is irreversible`)
    ) {
      const response = await this.props.dispatch(
        removeNote(this.state.note.id),
      );
      if (!response.error) {
        this.setState({ isDeleted: true });
      }
    }
  }

  /**
   * Render a note's body
   * @param {Object} note
   */
  renderBody() {
    const note = this.state.note;
    return (
      <>
        <div dangerouslySetInnerHTML={{ __html: note.html }} />
        {note.resolveable === 1 && (
          <div className="note-item--meta">
            <MiniUserList users={note.assignees} />
          </div>
        )}
        <div>
          <small>
            <DateTime date={note.created_at} />
            {this.hasPermission && (
              <>
                {this.state.note.resolved !== 1 && (
                  <>
                    &nbsp;&bull;&nbsp;
                    <a onClick={this.handleEdit} className="has-text-grey">
                      <Icon icon="pencil" size="small" /> Edit
                    </a>
                  </>
                )}
                &nbsp;&bull;&nbsp;
                <a onClick={this.handleDelete} className="has-text-grey">
                  <Icon icon="times" size="small" /> Delete
                </a>
              </>
            )}
          </small>
        </div>
      </>
    );
  }

  /**
   * Render a note's action buttons to the right
   */
  renderRight() {
    if (this.state.isEditing) {
      return (
        <div className="media-right">
          <Tooltip text="Cancel">
            <span onClick={this.handleUnedit}>
              <Icon icon="times" size="small" />
            </span>
          </Tooltip>
        </div>
      );
    }

    const note = this.state.note;
    return (
      <div className="media-right">
        {note.resolveable === 1 && (
          <Tooltip
            text={
              note.resolved === 1
                ? `Resolved ${moment(note.updated_at).fromNow()}`
                : 'Mark as resolved'
            }
          >
            <Button
              className={`button is-${
                note.resolved === 1 ? 'success' : 'default'
              }`}
              handleClick={() => {
                this.handleUpdate({ resolved: note.resolved ? 0 : 1 });
              }}
            >
              <Icon icon="check" />
            </Button>
          </Tooltip>
        )}
      </div>
    );
  }

  renderParentName(note) {
    switch (note.resource_type) {
      case 'bookings':
        return `${note.parent.contact_name || 'an unknown contact'}'s booking`;
      case 'vehicles':
        return note.parent.registration;
      case 'payments':
        return `a ${note.parent.type.toLowerCase()} payment of £${Number(
          note.parent.total,
        ).toFixed(2)}`;
      default:
        return note.parent.name;
    }
  }

  render() {
    if (this.state.isDeleted) {
      return null;
    }
    const note = this.state.note;
    return (
      <div
        className={`media note-item ${
          note.resolved === 1
            ? 'is-resolved'
            : note.resolveable === 1
              ? 'is-resolveable'
              : ''
        }`}
      >
        <div className="media-left is-hidden-touch">
          <Avatar size={48} email={note.user_email} />
        </div>
        <div className="media-content">
          <small>
            <strong>{note.user_name}</strong>
            {this.props.showParentInfo && (
              <span>
                &nbsp;on&nbsp;
                <Link to={`${this.state.parentPath}/${note.parent.id}`}>
                  {this.renderParentName(note)}
                </Link>
              </span>
            )}
          </small>
          {!this.state.isEditing ? (
            this.renderBody()
          ) : (
            <NoteForm note={note} handleUpdate={this.receiveUpdate} />
          )}
        </div>
        {this.renderRight()}
      </div>
    );
  }
}

const mapStateToProps = ({ auth }) => ({
  auth,
});

export default connect(mapStateToProps)(NoteItem);
